import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ArrowRight, BarChart3, Loader } from "lucide-react";

import {
  disconnect as disconnectApi,
  getConnection,
  getDashboard,
  getProperties,
  setDefaultProperty,
  setPropertyLabel,
  startConnect,
} from "../../api/googleAnalytics";
import { useAuth } from "../../contexts/AuthContext";
import { useUsage } from "../../hooks/useUsage";
import ConfirmDialog from "../ConfirmDialog";
import UpgradePrompt from "../UpgradePrompt";
import ChannelsTab from "./ChannelsTab";
import ConnectCard from "./ConnectCard";
import DashboardTabs from "./DashboardTabs";
import OverviewTab from "./OverviewTab";
import PropertySelector from "./PropertySelector";
import RangePills, { CUSTOM } from "./RangePills";
import TopLandingPagesTab from "./TopLandingPagesTab";
import TopPagesTab from "./TopPagesTab";
import TrafficTab from "./TrafficTab";
import { errorMessage } from "./errors";

const DEFAULT_RANGE = "28d";

const TABS = ["Overview", "Traffic", "Channels", "Top Pages", "Top Landing Pages"];

// A revoked Google grant comes back as 409 so it can be told apart from a plan
// problem (403) or a missing connection (404) and answered with a reconnect card.
const CONFLICT = 409;

// The one status that honestly means "no connection yet". Every other failure on
// the connection call is a real error and must not masquerade as "not connected".
const NOT_FOUND = 404;

const Centered = ({ children }) => (
  <div className="flex items-center justify-center py-20 gap-3">{children}</div>
);

const GoogleAnalyticsDashboard = () => {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const navigate = useNavigate();
  const { byFeature, loading: usageLoading } = useUsage();

  const [connection, setConnection] = useState(null);
  const [connectionLoading, setConnectionLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState(null);

  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const [propertyId, setPropertyId] = useState(null);
  const [range, setRange] = useState(DEFAULT_RANGE);
  // Only set once a custom range is applied; presets leave it null so the server
  // falls back to ?range=.
  const [customRange, setCustomRange] = useState(null);
  const [refreshingProperties, setRefreshingProperties] = useState(false);

  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS[0]);

  const included = byFeature ? byFeature.google_analytics?.included !== false : true;

  const loadConnection = useCallback(async () => {
    // Anonymous visitors arrive here from the footer and the home page, and every
    // Analytics endpoint is authenticated — asking would only yield a 401 error card.
    if (!userId) {
      setConnectionLoading(false);
      return;
    }
    setConnectionLoading(true);
    setConnectionError(null);
    try {
      const res = await getConnection();
      setConnection(res.data);
      setPropertyId(res.data.default_property_id ?? null);
      setNeedsReconnect(res.data.status === "revoked");
    } catch (err) {
      // A 404 is the honest "no connection yet" signal; any other status is a real
      // failure and must surface, not silently render the Connect card.
      if (err?.response?.status === NOT_FOUND) {
        setConnection(null);
      } else {
        setConnectionError(errorMessage(err, "Could not load your Google Analytics connection."));
      }
    } finally {
      setConnectionLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadConnection();
  }, [loadConnection]);

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true);
    setDashboardError(null);
    try {
      const res = await getDashboard({
        propertyId,
        // CUSTOM is a UI-only value; the API's ?range= accepts presets only.
        range: range === CUSTOM ? undefined : range,
        startDate: customRange?.startDate,
        endDate: customRange?.endDate,
      });
      setDashboard(res.data);
      setNeedsReconnect(false);
    } catch (err) {
      if (err?.response?.status === CONFLICT) {
        setNeedsReconnect(true);
      } else {
        setDashboardError(errorMessage(err, "Could not load Google Analytics data."));
      }
      setDashboard(null);
    } finally {
      setDashboardLoading(false);
    }
  }, [propertyId, range, customRange]);

  const connected = !!connection && !needsReconnect;
  // Selecting Custom only reveals the date inputs — there is nothing to query until
  // they are applied, so the currently shown data stays put instead of being refetched.
  const awaitingCustomDates = range === CUSTOM && !customRange;

  useEffect(() => {
    if (!connected || !included || awaitingCustomDates) return;
    loadDashboard();
  }, [connected, included, awaitingCustomDates, loadDashboard]);

  const handleConnect = async () => {
    setConnecting(true);
    setConnectError(null);
    try {
      const res = await startConnect();
      // Full-page handoff to Google's consent screen, as with Stripe checkout.
      window.location.href = res.data.auth_url;
    } catch (err) {
      setConnectError(errorMessage(err, "Could not start the Google connection."));
      setConnecting(false);
    }
  };

  const handleSelectProperty = async (nextPropertyId) => {
    setPropertyId(nextPropertyId);
    try {
      // Persist server-side so the choice survives a reload and other devices.
      const res = await setDefaultProperty(nextPropertyId);
      setConnection(res.data);
    } catch (err) {
      setDashboardError(errorMessage(err, "Could not switch property."));
    }
  };

  const handleSelectPreset = (next) => {
    setRange(next);
    // Selecting a preset must drop any applied custom window, or the server would keep
    // honouring the explicit dates and the pills would lie about what is shown.
    if (next !== CUSTOM) setCustomRange(null);
  };

  const handleApplyCustom = ({ startDate, endDate }) => {
    setRange(CUSTOM);
    setCustomRange({ startDate, endDate });
  };

  const handleRenameProperty = async (targetPropertyId, label) => {
    try {
      const res = await setPropertyLabel(targetPropertyId, label);
      setConnection(res.data);
    } catch (err) {
      setDashboardError(errorMessage(err, "Could not rename the property."));
    }
  };

  const handleRefreshProperties = async () => {
    setRefreshingProperties(true);
    try {
      const res = await getProperties(true);
      setConnection((prev) => ({ ...prev, properties: res.data.properties }));
    } catch (err) {
      setDashboardError(errorMessage(err, "Could not refresh properties."));
    } finally {
      setRefreshingProperties(false);
    }
  };

  const handleConfirmDisconnect = async () => {
    setDisconnecting(true);
    try {
      await disconnectApi();
      setConnection(null);
      setDashboard(null);
      setPropertyId(null);
      setNeedsReconnect(false);
      setDisconnectDialogOpen(false);
    } catch (err) {
      setDashboardError(errorMessage(err, "Could not disconnect Google Analytics."));
      setDisconnectDialogOpen(false);
    } finally {
      setDisconnecting(false);
    }
  };

  const renderBody = () => {
    if (!userId) {
      return (
        <div className="bg-surface-container-low rounded-2xl p-8 border border-surface-container-high max-w-2xl">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-5">
            <BarChart3 className="w-6 h-6 text-primary" />
          </div>
          <h2 className="font-headline text-xl font-bold text-on-surface">
            Log in to connect Google Analytics
          </h2>
          <p className="text-sm text-on-surface-variant mt-2">
            Link a GA4 property to see traffic, engagement and conversions alongside your AI
            visibility reports. Read-only access, and you can disconnect at any time.
          </p>
          <button
            type="button"
            onClick={() => navigate("?auth=login")}
            className="mt-6 px-5 py-2.5 bg-primary text-on-primary rounded-xl text-sm font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
          >
            Log In
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      );
    }

    if (usageLoading || connectionLoading) {
      return (
        <Centered>
          <Loader className="w-6 h-6 animate-spin text-primary" />
          <span className="text-on-surface-variant text-sm">Loading…</span>
        </Centered>
      );
    }

    if (!included) {
      return (
        <UpgradePrompt requiredPlan="optimized" featureName="the Google Analytics dashboard" />
      );
    }

    if (connectionError) {
      return (
        <div className="bg-error-container border border-error/20 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3 max-w-2xl">
          <p className="text-sm text-on-error-container">{connectionError}</p>
          <button
            type="button"
            onClick={loadConnection}
            className="px-4 py-2 bg-primary text-on-primary rounded-xl text-sm font-bold hover:opacity-90 transition-opacity"
          >
            Try again
          </button>
        </div>
      );
    }

    if (!connection || needsReconnect) {
      return (
        <ConnectCard
          onConnect={handleConnect}
          connecting={connecting}
          reconnect={needsReconnect}
          error={connectError}
        />
      );
    }

    if (connection.properties?.length === 0) {
      return (
        <div className="bg-surface-container-low rounded-2xl p-10 border border-surface-container-high text-center">
          <p className="text-sm text-on-surface-variant">
            No GA4 properties were found on {connection.google_email || "your Google account"}.
            Create a property in Google Analytics, then refresh.
          </p>
          <button
            type="button"
            onClick={handleRefreshProperties}
            disabled={refreshingProperties}
            className="mt-4 px-4 py-2 bg-primary text-on-primary rounded-xl text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            Refresh properties
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <PropertySelector
            properties={connection.properties ?? []}
            value={propertyId ?? connection.default_property_id}
            onChange={handleSelectProperty}
            onRefresh={handleRefreshProperties}
            onRename={handleRenameProperty}
            refreshing={refreshingProperties}
            disabled={dashboardLoading}
          />
          <RangePills
            value={range}
            custom={customRange}
            onSelectPreset={handleSelectPreset}
            onApplyCustom={handleApplyCustom}
            disabled={dashboardLoading}
          />
        </div>

        {dashboardError && (
          <div className="bg-error-container border border-error/20 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-on-error-container">{dashboardError}</p>
            <button
              type="button"
              onClick={loadDashboard}
              className="px-4 py-2 bg-primary text-on-primary rounded-xl text-sm font-bold hover:opacity-90 transition-opacity"
            >
              Try again
            </button>
          </div>
        )}

        {dashboardLoading && !dashboard ? (
          <Centered>
            <Loader className="w-6 h-6 animate-spin text-primary" />
            <span className="text-on-surface-variant text-sm">Loading Analytics data…</span>
          </Centered>
        ) : (
          dashboard && (
            <div className="relative">
              {dashboardLoading && (
                <div className="absolute top-0 right-0 z-10 flex items-center gap-2 text-xs text-on-surface-variant bg-surface-container-low/90 backdrop-blur-sm px-3 py-1.5 rounded-full border border-surface-container-high shadow-sm">
                  <Loader className="w-3.5 h-3.5 animate-spin text-primary" />
                  Updating…
                </div>
              )}
              <div
                className={`space-y-4 transition-opacity ${dashboardLoading ? "opacity-50 pointer-events-none" : ""}`}
              >
                <DashboardTabs tabs={TABS} activeTab={activeTab} onSelect={setActiveTab} />

                {activeTab === "Overview" && <OverviewTab kpis={dashboard.kpis} />}
                {activeTab === "Traffic" && <TrafficTab timeseries={dashboard.timeseries} />}
                {activeTab === "Channels" && <ChannelsTab channels={dashboard.channels} />}
                {activeTab === "Top Pages" && <TopPagesTab topPages={dashboard.top_pages} />}
                {activeTab === "Top Landing Pages" && (
                  <TopLandingPagesTab landingPages={dashboard.landing_pages} />
                )}
              </div>
            </div>
          )
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen pt-6 pb-8 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div>
            <h1 className="font-headline text-2xl font-bold text-on-surface">Google Analytics</h1>
            <p className="text-sm text-on-surface-variant mt-1">
              Traffic, engagement and conversions from your GA4 property.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {dashboard && (
              <p className="text-xs text-on-surface-variant">
                {dashboard.range.start_date} → {dashboard.range.end_date}
                {dashboard.time_zone ? ` · ${dashboard.time_zone}` : ""}
              </p>
            )}
            {connected && included && (
              <button
                type="button"
                onClick={() => setDisconnectDialogOpen(true)}
                className="px-4 py-2 bg-error text-on-error rounded-xl text-sm font-bold hover:opacity-90 transition-opacity"
              >
                Disconnect
              </button>
            )}
          </div>
        </div>

        {renderBody()}
      </div>

      <ConfirmDialog
        open={disconnectDialogOpen}
        onOpenChange={setDisconnectDialogOpen}
        title="Disconnect Google Analytics?"
        description="You'll need to reconnect and grant access again to restore the dashboard."
        confirmLabel="Disconnect"
        onConfirm={handleConfirmDisconnect}
        loading={disconnecting}
        variant="danger"
      />
    </div>
  );
};

export default GoogleAnalyticsDashboard;
